import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { UserPlus, Pencil, Trash2, Check, Loader2, ShieldCheck, PencilLine } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import { useCan } from '@/features/auth'
import {
  fetchAdminUsers,
  createAdminUser,
  updateAdminUser,
  deleteAdminUser,
  ROLE_LABELS,
  type AdminUser,
  type AdminRole,
} from '@/features/admin-users/adminUsers'

const emptyDraft = (): AdminUser => ({ id: '', email: '', name: '', role: 'EDITOR', active: true })

function initials(name: string, email: string) {
  const base = name.trim() || email
  return base.split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || '?'
}

export function UsersPage() {
  const canEdit = useCan('admins:manage')
  const [users, setUsers] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [reloadKey, setReloadKey] = useState(0)
  const [draft, setDraft] = useState<AdminUser | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!canEdit) return
    let active = true
    setLoading(true)
    fetchAdminUsers()
      .then((d) => active && setUsers(d))
      .catch(() => active && toast.error('No se pudieron cargar los usuarios'))
      .finally(() => active && setLoading(false))
    return () => {
      active = false
    }
  }, [canEdit, reloadKey])

  if (!canEdit) {
    return (
      <div className="rounded-card border border-line bg-panel p-8 text-center">
        <h2 className="text-lg font-semibold text-ink">Acceso restringido</h2>
        <p className="mt-1 text-sm text-ink-muted">Solo los administradores pueden gestionar usuarios.</p>
      </div>
    )
  }

  const reload = () => setReloadKey((k) => k + 1)

  const save = async () => {
    if (!draft) return
    setSaving(true)
    const input = { email: draft.email.trim(), name: draft.name.trim(), role: draft.role, active: draft.active }
    try {
      if (draft.id) await updateAdminUser(draft.id, input)
      else await createAdminUser(input)
      setDraft(null)
      reload()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'No se pudo guardar el usuario')
    } finally {
      setSaving(false)
    }
  }

  const remove = async (u: AdminUser) => {
    if (!window.confirm(`¿Eliminar a ${u.name || u.email}?`)) return
    try {
      await deleteAdminUser(u.id)
      reload()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'No se pudo eliminar el usuario')
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-serif text-[36px] font-normal leading-[1.1] tracking-tight text-ink">Usuarios</h1>
          <p className="mt-1 max-w-xl text-sm text-ink-muted">
            Personas con permiso de administración del landing. <strong>Administrador</strong>: gestiona todo.{' '}
            <strong>Editor</strong>: gestiona el contenido de "Lo último".
          </p>
        </div>
        <button onClick={() => setDraft(emptyDraft())} className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-dark">
          <UserPlus className="size-4" /> Invitar usuario
        </button>
      </header>

      {loading ? (
        <div className="flex items-center justify-center rounded-card border border-line bg-panel py-16 text-ink-muted">
          <Loader2 className="mr-2 size-5 animate-spin" /> Cargando…
        </div>
      ) : (
        <ul className="space-y-2">
          {users.map((u) => (
            <li key={u.id} className="flex items-center gap-4 rounded-card border border-line bg-panel p-3">
              <span className="grid size-11 shrink-0 place-items-center rounded-full bg-brand-soft text-sm font-bold text-brand">
                {initials(u.name, u.email)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="truncate text-sm font-semibold text-ink">{u.name || u.email}</h3>
                  {!u.active && <span className="rounded-full bg-surface px-1.5 py-0.5 text-[9px] font-bold uppercase text-ink-subtle">Inactivo</span>}
                </div>
                <p className="truncate text-xs text-ink-muted">{u.email}</p>
              </div>
              <RoleBadge role={u.role} />
              <div className="flex shrink-0 items-center gap-1">
                <button onClick={() => setDraft({ ...u })} className="grid size-8 place-items-center rounded-lg text-ink-muted hover:bg-surface hover:text-ink" title="Editar">
                  <Pencil className="size-4" />
                </button>
                <button onClick={() => void remove(u)} className="grid size-8 place-items-center rounded-lg text-ink-subtle hover:bg-danger-soft hover:text-danger" title="Eliminar">
                  <Trash2 className="size-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Alta / edición */}
      <Dialog open={!!draft} onOpenChange={(o) => !o && setDraft(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{draft?.id ? 'Editar usuario' : 'Invitar usuario'}</DialogTitle>
          </DialogHeader>
          {draft && (
            <div className="space-y-4">
              <Field label="Correo electrónico" type="email" value={draft.email} onChange={(v) => setDraft({ ...draft, email: v })} placeholder="persona@tecopos.com" />
              <Field label="Nombre" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} placeholder="Nombre y apellidos" />
              <div>
                <span className="text-xs font-semibold text-ink-muted">Rol</span>
                <div className="mt-1.5 grid grid-cols-2 gap-2">
                  {(['ADMIN', 'EDITOR'] as AdminRole[]).map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setDraft({ ...draft, role: r })}
                      className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                        draft.role === r ? 'border-brand bg-brand-soft text-ink' : 'border-line text-ink-muted hover:bg-surface'
                      }`}
                    >
                      {r === 'ADMIN' ? <ShieldCheck className="size-4 shrink-0" /> : <PencilLine className="size-4 shrink-0" />}
                      <span className="text-xs font-semibold">{ROLE_LABELS[r]}</span>
                    </button>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-ink">
                <input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} className="size-4 accent-[color:var(--brand-orange)]" />
                Activo
              </label>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setDraft(null)} className="text-sm font-medium text-ink-muted hover:text-ink">Cancelar</button>
            <button
              onClick={() => void save()}
              disabled={saving || !draft?.email.trim()}
              className="inline-flex items-center gap-2 rounded-lg bg-brand px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-dark disabled:opacity-50"
            >
              {saving ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function RoleBadge({ role }: { role: AdminRole }) {
  const admin = role === 'ADMIN'
  return (
    <span className={`hidden shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase sm:inline-flex ${admin ? 'bg-brand-soft text-brand' : 'bg-info-soft text-info'}`}>
      {admin ? <ShieldCheck className="size-3" /> : <PencilLine className="size-3" />}
      {ROLE_LABELS[role]}
    </span>
  )
}

function Field({ label, value, onChange, type = 'text', placeholder }: { label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-ink-muted">{label}</span>
      <input type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="mt-1.5 w-full rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink outline-none focus:border-brand" />
    </label>
  )
}
